import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle2,
  XCircle,
  Brain,
  Heart,
  RefreshCw,
  AlertTriangle,
  Sparkles
} from "lucide-react";
import StepWrapper from "./StepWrapper";
import { cn } from "@/lib/utils";

const YesNoButton = ({ value, onChange, yesLabel = "Yes", noLabel = "No" }) => {
  return (
    <div className="flex gap-3">
      <Button
        type="button"
        variant={value === true ? "default" : "outline"}
        onClick={() => onChange(true)}
        className={cn(
          "flex-1 py-6",
          value === true && "bg-success hover:bg-success/90 text-success-foreground"
        )}
      >
        <CheckCircle2 className="w-5 h-5 mr-2" />
        {yesLabel}
      </Button>
      <Button
        type="button"
        variant={value === false ? "default" : "outline"}
        onClick={() => onChange(false)}
        className={cn(
          "flex-1 py-6",
          value === false && "bg-destructive hover:bg-destructive/90 text-destructive-foreground"
        )}
      >
        <XCircle className="w-5 h-5 mr-2" />
        {noLabel}
      </Button>
    </div>
  );
};

const StepTestCommit = ({ data, updateData, onNext, onBack, onIterate, coachMode = true }) => {
  const { ratings, amIFitToDecide, amIReadyToDecide, doesItMakeSense, doesItFeelRight } = data;
  
  const weakestLink = Math.min(...Object.values(ratings));
  const allQuestionsAnswered = 
    amIFitToDecide !== null && 
    amIReadyToDecide !== null && 
    doesItMakeSense !== null && 
    doesItFeelRight !== null;
  
  const isReadyToDecide = 
    amIFitToDecide === true && 
    amIReadyToDecide === true && 
    doesItMakeSense === true && 
    doesItFeelRight === true;

  return (
    <StepWrapper
      stepNumber={5}
      title="Test & Commit"
      subtitle={coachMode ? "Check if you're ready to make this decision. A good decision makes sense AND feels right." : null}
    >
      <div className="space-y-6">
        {/* Decision Chain Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className={cn(
            "p-5 bg-card border-border shadow-card",
            weakestLink < 50 && "border-warning/50",
            weakestLink >= 70 && "border-success/50"
          )}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Your weakest link is at</p>
                <p className={cn(
                  "text-3xl font-bold",
                  weakestLink < 50 ? "text-warning" : weakestLink < 70 ? "text-accent-warm" : "text-success"
                )}>
                  {weakestLink}%
                </p>
              </div>
              {weakestLink < 50 && (
                <div className="flex items-center gap-2 text-warning bg-warning/10 px-4 py-2 rounded-lg">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="text-sm font-medium">Consider strengthening weak links</span>
                </div>
              )}
              {weakestLink >= 70 && (
                <div className="flex items-center gap-2 text-success bg-success/10 px-4 py-2 rounded-lg">
                  <Sparkles className="w-5 h-5" />
                  <span className="text-sm font-medium">Strong decision foundation!</span>
                </div>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Fitness Check */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="p-6 bg-card border-border shadow-card">
            <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary" />
              Am I fit to decide?
            </h3>
            {coachMode && (
              <p className="text-sm text-muted-foreground mb-4">
                Consider if you&apos;re in the right state of mind — not too tired, stressed, or rushed.
              </p>
            )}
            <YesNoButton
              value={amIFitToDecide}
              onChange={(value) => updateData("amIFitToDecide", value)}
            />
          </Card>
        </motion.div>

        {/* Readiness Check */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="p-6 bg-card border-border shadow-card">
            <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              Am I ready to decide?
            </h3>
            {coachMode && (
              <p className="text-sm text-muted-foreground mb-4">
                Do you have enough information? Have you explored your options sufficiently?
              </p>
            )}
            <YesNoButton
              value={amIReadyToDecide}
              onChange={(value) => updateData("amIReadyToDecide", value)}
            />
          </Card>
        </motion.div>

        {/* Sense Check */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="p-6 bg-card border-border shadow-card">
            <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <Brain className="w-5 h-5 text-chain-reasoning" />
              Does this decision make sense?
            </h3>
            {coachMode && (
              <p className="text-sm text-muted-foreground mb-4">
                Logically, does this choice align with your goals and values?
              </p>
            )}
            <YesNoButton
              value={doesItMakeSense}
              onChange={(value) => updateData("doesItMakeSense", value)}
            />
          </Card>
        </motion.div>

        {/* Feel Check */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="p-6 bg-card border-border shadow-card">
            <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <Heart className="w-5 h-5 text-chain-values" />
              Does this decision feel right?
            </h3>
            {coachMode && (
              <p className="text-sm text-muted-foreground mb-4">
                Trust your gut. Are you at peace with this direction?
              </p>
            )}
            <YesNoButton
              value={doesItFeelRight}
              onChange={(value) => updateData("doesItFeelRight", value)}
            />
          </Card>
        </motion.div>

        {/* Result Summary */}
        {allQuestionsAnswered && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Card className={cn(
              "p-6 border-2",
              isReadyToDecide ? "bg-success/5 border-success/30" : "bg-warning/5 border-warning/30"
            )}>
              {isReadyToDecide ? (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-success" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    You're Ready to Decide!
                  </h3>
                  <p className="text-muted-foreground">
                    Your decision makes sense and feels right. Time to commit and take action.
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-warning/20 flex items-center justify-center mx-auto mb-4">
                    <RefreshCw className="w-8 h-8 text-warning" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Consider Iterating
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Some areas could use more attention. Consider going back to strengthen your weaker links.
                  </p>
                  <Button
                    variant="outline"
                    onClick={onIterate}
                    className="border-warning text-warning hover:bg-warning/10"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Go Back & Strengthen
                  </Button>
                </div>
              )}
            </Card>
          </motion.div>
        )}

        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex justify-between items-center pt-4"
        >
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Button
            onClick={onNext}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-6"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    </StepWrapper>
  );
};

export default StepTestCommit;
